import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from './ui/button';
import { Sheet, SheetContent } from './ui/sheet';
import ContactModal from './ContactModal';

import './MobileProductHeader.css';

const actionStyles = {
  default:
    'text-sm bg-gray-900 text-white hover:bg-gray-800 transition-all duration-300 rounded-lg shadow-none',
  outline:
    'text-sm border-gray-200 hover:bg-gray-200 transition-all duration-300 rounded-lg',
  blackOutline:
    'text-sm border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300 rounded-lg',
};

export default function MobileProductHeader({
  scrolled,
  isWrityPage = false,
  alwaysBorder = false,
  rightBrand,
  links,
  action,
  highlightedAction,
}) {
  const navigate = useNavigate();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);

  const resolvedLinks = links ?? (
    isWrityPage
      ? [{ label: 'Back to Home', to: '/' }]
      : [{ label: 'Other Products', href: '/#products' }]
  );

  const resolvedAction = action ?? {
    label: 'Contact',
    variant: 'blackOutline',
    onClick: () => setIsContactOpen(true),
  };

  const resolvedHighlightedAction = highlightedAction ?? null;

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const handleItemClick = (item) => {
    if (typeof item?.onClick === 'function') {
      item.onClick();
      return;
    }

    if (item?.to) {
      navigate(item.to);
      requestAnimationFrame(scrollToTop);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const lastY = lastScrollYRef.current;

      if (currentY <= 12) {
        setIsHeaderVisible(true);
      } else if (currentY < lastY - 6) {
        setIsHeaderVisible(true);
      } else if (currentY > lastY + 6) {
        setIsHeaderVisible(false);
      }

      lastScrollYRef.current = currentY;
    };

    lastScrollYRef.current = window.scrollY;
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const headerClassName = [
    'mobileProductHeader',
    isHeaderVisible ? 'mobileProductHeader--visible' : 'mobileProductHeader--hidden',
    (Boolean(scrolled) || alwaysBorder) ? 'border-b border-gray-200/50' : '',
  ].join(' ');

  return (
    <>
      <div className={headerClassName}>
        <div className="h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <a
              href="/"
              aria-label="Back to Longfloat home"
              className="w-9 h-9 rounded-full flex items-center justify-center text-slate-900"
            >
              <ArrowLeft size={18} />
            </a>

            {rightBrand && rightBrand.logoSrc ? (
              <button
                type="button"
                onClick={() => handleItemClick(rightBrand)}
                className="flex items-center justify-center bg-transparent border-0 p-0"
              >
                <img
                  src={rightBrand.logoSrc}
                  alt={rightBrand.alt || rightBrand.title || 'Product logo'}
                  className={rightBrand.logoClassName || 'h-6 w-auto'}
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex items-center gap-2 bg-transparent border-0 p-0"
              >
                <img src="/Logo.svg" alt="Longfloat Logo" className="w-7 h-7 object-contain" />
                <span className="text-xl font-light tracking-tight text-gray-900">Longfloat</span>
              </button>
            )}
          </div>

          <button
            type="button"
            aria-label="Open menu"
            className="w-15 h-15 bg-transparent shadow-none rounded-none flex items-center justify-center text-slate-900 appearance-none"
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="z-[120]">
          <div className="flex flex-col gap-3 pt-10">
            {resolvedLinks.map((link, index) => {
              if (link.href) {
                return (
                  <a
                    key={`${link.label}-${index}`}
                    href={link.href}
                    target={link.target}
                    rel={link.rel}
                    className="text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
                    onClick={(e) => {
                      if (typeof link.onClick === 'function') link.onClick(e);
                      setMobileMenuOpen(false);
                    }}
                  >
                    {link.label}
                  </a>
                );
              }

              return (
                <button
                  key={`${link.label}-${index}`}
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleItemClick(link);
                  }}
                  className="text-left text-sm text-gray-600 hover:text-gray-900 transition-colors duration-300"
                >
                  {link.label}
                </button>
              );
            })}

            {(resolvedAction || resolvedHighlightedAction) && (
              <div className="pt-2 mt-2 flex flex-col gap-3">
                {resolvedAction && (
                  <Button
                    variant={resolvedAction.variant || 'outline'}
                    className={actionStyles[resolvedAction.variant || 'blackOutline'] || actionStyles.blackOutline}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      resolvedAction.onClick?.();
                    }}
                  >
                    {resolvedAction.label}
                  </Button>
                )}

                {resolvedHighlightedAction && (
                  <Button
                    variant={resolvedHighlightedAction.variant || 'default'}
                    className={
                      actionStyles[resolvedHighlightedAction.variant || 'default'] || actionStyles.default
                    }
                    onClick={() => {
                      setMobileMenuOpen(false);
                      resolvedHighlightedAction.onClick?.();
                    }}
                  >
                    {resolvedHighlightedAction.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <ContactModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </>
  );
}
